import React, { useEffect, useMemo, useRef, useState } from "react";
import { Rnd } from "react-rnd";

import styled from "styled-components";
import { ShapefileLegendData, ShapefileLegendGroup } from "types/maps";
import { parseNumber } from "utils/fp";

import { useLocalStorage } from "hooks";

import { getLegendItemSize } from "components/screenshot/utils";
import {
  LegendDimensionsT,
  LegendPositionT,
  LegendT
} from "components/shared/charts/legend/Legend";
import getCalculatedLegend from "components/shared/charts/legend/utils/getCalculatedLegend";
import {
  LegendPosition,
  getPositionFromChartSettings
} from "components/shared/charts/legend/utils/getPositionFromChartSettings";

const DIMENSIONS_KEY = "map-screenshot-shapefile-legend-dimensions";
const POSITION_KEY = "map-screenshot-shapefile-legend-position";

const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 250;

export interface ShapefileLegendBaseProps {
  readonly legendData: ShapefileLegendData;
  readonly showLegend: boolean;
  readonly showBorder: boolean;
  readonly titleSize: number;
  readonly labelsSize: number;
  readonly backgroundOpacity: number;
  readonly parentDimensions: { width: number; height: number };
  readonly mapScreenshotScale: number;
  readonly isScreenshotOverlayVisible: boolean;
  readonly isWrappingText?: boolean;
}

/** Insert zero-width space after special characters to allow line breaks. */
const addBreakOpportunities = (text: string): string =>
  text.replace(/([_\-./\\])/g, "$1\u200B");

const renderGroup = (
  group: ShapefileLegendGroup,
  labelsSize: number,
  groupIndex: number,
  isWrappingText: boolean
) => (
  <GroupContainer key={groupIndex}>
    {group.heading && <GroupHeading $fontSize={labelsSize}>{group.heading}</GroupHeading>}
    <GroupItems $indented={Boolean(group.heading)}>
      {group.items.map((item, i) => (
        <LegendItem key={`${groupIndex}-${i}`} $fontSize={labelsSize}>
          <ColorSwatch
            $color={item.color}
            $strokeColor={item.strokeColor}
            $strokeWidth={item.strokeWidth}
            $size={labelsSize}
          />
          <ItemLabel $isWrappingText={isWrappingText}>
            {isWrappingText ? addBreakOpportunities(item.label) : item.label}
          </ItemLabel>
        </LegendItem>
      ))}
    </GroupItems>
  </GroupContainer>
);

export const ShapefileLegendBase: React.FC<ShapefileLegendBaseProps> = ({
  legendData,
  showLegend,
  showBorder,
  titleSize,
  labelsSize,
  backgroundOpacity,
  parentDimensions,
  mapScreenshotScale,
  isScreenshotOverlayVisible,
  isWrappingText = true
}) => {
  const rndRef = useRef<Rnd>(null);

  const { getStoredValue: getStoredDimensions } = useLocalStorage(
    DIMENSIONS_KEY,
    undefined
  );
  const { getStoredValue: getStoredPosition } = useLocalStorage(POSITION_KEY, undefined);
  const [initialStoredPosition] = useState<Maybe<LegendPositionT>>(() =>
    getStoredPosition()
  );
  const hasCustomPositionRef = useRef(Boolean(initialStoredPosition));

  const totalItems = legendData.groups.reduce((sum, g) => sum + g.items.length, 0);

  const [dimensions, setDimensions] = useState<LegendDimensionsT>(() => {
    const stored = getStoredDimensions();
    if (stored) return stored;

    const itemSize = getLegendItemSize(isScreenshotOverlayVisible, labelsSize);
    const itemsHeight = totalItems * itemSize;
    return {
      width: DEFAULT_WIDTH,
      height: itemsHeight ? itemsHeight + 40 : DEFAULT_HEIGHT
    };
  });

  const [position, setPosition] = useState<LegendPositionT>(() => {
    return (
      initialStoredPosition ??
      getPositionFromChartSettings(dimensions, parentDimensions, LegendPosition.SE)
    );
  });

  const handleDragStop = (_: unknown, dragElement: { x: number; y: number }) => {
    const next = { x: dragElement.x, y: dragElement.y };
    hasCustomPositionRef.current = true;
    setPosition(next);
    localStorage.setItem(POSITION_KEY, JSON.stringify(next));
  };

  const handleResize = (
    _e: unknown,
    _dir: unknown,
    ref: HTMLElement,
    _delta: unknown,
    p: LegendPositionT
  ) => {
    const nextDimensions = { width: ref.offsetWidth, height: ref.offsetHeight };
    hasCustomPositionRef.current = true;
    setDimensions(nextDimensions);
    setPosition(p);
    localStorage.setItem(DIMENSIONS_KEY, JSON.stringify(nextDimensions));
    localStorage.setItem(POSITION_KEY, JSON.stringify(p));
  };

  const currentLegend: LegendT = { ...position, ...dimensions };
  const calculatedLegend = getCalculatedLegend(currentLegend, parentDimensions);

  const currentPosition = useMemo(
    () =>
      hasCustomPositionRef.current
        ? { x: calculatedLegend.x, y: calculatedLegend.y }
        : getPositionFromChartSettings(
            { width: calculatedLegend.width, height: calculatedLegend.height },
            parentDimensions,
            LegendPosition.SE
          ),
    [calculatedLegend, parentDimensions]
  );

  useEffect(() => {
    if (rndRef.current) {
      rndRef.current.updatePosition({ x: currentPosition.x, y: currentPosition.y });
    }
  }, [currentPosition.x, currentPosition.y]);

  if (!showLegend || !legendData.hasData) {
    return null;
  }

  const scaledScale =
    parseNumber(mapScreenshotScale, 1) < 1 && isScreenshotOverlayVisible
      ? parseNumber(mapScreenshotScale, 1)
      : 1;

  return (
    <StyledRnd
      bounds="parent"
      position={{ x: currentPosition.x, y: currentPosition.y }}
      size={{ width: calculatedLegend.width, height: calculatedLegend.height }}
      onDragStop={handleDragStop}
      onResize={handleResize}
      $mapScreenshotScale={scaledScale}
      $showBorder={showBorder}
      $backgroundOpacity={backgroundOpacity}
      ref={rndRef}>
      <ContentWrapper>
        <Title $fontSize={titleSize} $isWrappingText={isWrappingText}>
          {legendData.title}
        </Title>
        <ItemsContainer>
          {legendData.groups.map((group, i) =>
            renderGroup(group, labelsSize, i, isWrappingText)
          )}
        </ItemsContainer>
      </ContentWrapper>
    </StyledRnd>
  );
};

const StyledRnd = styled(Rnd)<{
  $mapScreenshotScale: number;
  $showBorder: boolean;
  $backgroundOpacity: number;
}>`
  display: grid;
  overflow: hidden;
  min-height: ${(p) => (p.$mapScreenshotScale ?? 1) * 30}px;
  min-width: ${(p) => (p.$mapScreenshotScale ?? 1) * 150}px;
  background: ${(p) => `rgba(255, 255, 255, ${p.$backgroundOpacity ?? 1})`};
  border: ${(p) => (p.$showBorder ? "1px solid gray" : "none")};
  position: relative;
  z-index: 9999;

  & > div:last-child {
    position: unset;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: inline-flex;
  flex-direction: column;
  padding: 5px 7px 5px 5px;
  overflow: hidden auto;
`;

const Title = styled.div<{ $fontSize: number; $isWrappingText?: boolean }>`
  font-size: ${(p) => p.$fontSize / 10}rem;
  font-weight: bold;
  padding: 2px 5px 4px;
  white-space: ${(p) => (p.$isWrappingText ? "normal" : "nowrap")};
  word-break: ${(p) => (p.$isWrappingText ? "break-word" : "normal")};
`;

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupHeading = styled.div<{ $fontSize: number }>`
  font-size: ${(p) => p.$fontSize / 10}rem;
  font-weight: 600;
  padding: 4px 5px 2px;
  color: #555;
`;

const GroupItems = styled.div<{ $indented: boolean }>`
  display: flex;
  flex-direction: column;
  padding-left: ${(p) => (p.$indented ? "10px" : "0")};
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden auto;
`;

const LegendItem = styled.div<{ $fontSize: number }>`
  font-size: ${(p) => (p.$fontSize ?? 14) / 10}rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 5px;
  gap: 8px;
`;

const ColorSwatch = styled.div<{
  $color: string;
  $strokeColor?: string;
  $strokeWidth?: number;
  $size: number;
}>`
  min-width: ${(p) => Math.max(12, p.$size)}px;
  min-height: ${(p) => Math.max(12, p.$size)}px;
  width: ${(p) => Math.max(12, p.$size)}px;
  height: ${(p) => Math.max(12, p.$size)}px;
  background-color: ${(p) => p.$color};
  border: ${(p) => {
    const width = p.$strokeWidth ?? 2;
    return p.$strokeColor
      ? `${width}px solid ${p.$strokeColor}`
      : `${width}px solid rgba(0, 0, 0, 0.15)`;
  }};
  border-radius: 2px;
`;

const ItemLabel = styled.span<{ $isWrappingText?: boolean }>`
  flex: 1;
  overflow: hidden;
  word-break: ${(p) => (p.$isWrappingText ? "break-word" : "normal")};
  white-space: ${(p) => (p.$isWrappingText ? "normal" : "nowrap")};
  text-overflow: ${(p) => (p.$isWrappingText ? "unset" : "ellipsis")};
`;
